import type { ImageProps } from "~www/types/sanity-image";

import { getImageDimensions } from "@sanity/asset-utils";
import { urlFor } from "~www/lib/sanity/sanity-images";

type NumberLike = number | `${number}`;
type SanityImageSource = ImageProps["asset"];

export const getSanityImageDimensions = ({
  asset,
  width,
  height,
}: {
  asset: SanityImageSource;
  width: NumberLike;
  height: NumberLike;
}) => {
  // Intrinsic dimensions come from the original asset reference (encoded in
  // its ID) and are unaffected by crop/hotspot, so only the reference is
  // needed here. The full source (with crop/hotspot) is passed to urlFor
  // separately so Studio crops are applied to the generated CDN URL.
  const dimensions = getImageDimensions(asset.asset);

  const widthProp = width ? Number(width) : dimensions.width;
  // don't use a larger image width than the actual image width
  const imageWidth =
    widthProp && widthProp >= dimensions.width ? dimensions.width : widthProp;

  const heightProp = height ? Number(height) : dimensions.height;
  // don't use a larger image height than the actual image height
  const imageHeight =
    heightProp && heightProp >= dimensions.height
      ? dimensions.height
      : heightProp;

  return { imageHeight, imageWidth };
};

export const getAssetProps = ({
  asset,
  width,
  height,
  quality,
}: {
  asset: SanityImageSource;
  width: NumberLike;
  height: NumberLike;
  quality: NumberLike;
}) => {
  const { imageWidth, imageHeight } = getSanityImageDimensions({
    asset,
    height,
    width,
  });

  return {
    imageHeight,
    imageWidth,
    url: urlFor(asset)
      .size(imageWidth, imageHeight)
      .dpr(2)
      .auto("format")
      .quality(Number(quality))
      .url(),
  };
};
